//external libs
import React, { memo, ReactElement, useCallback, useContext } from "react";

//components
import { FallbackAlert } from "./components/fallback-alert";

//helpers
import { isWalletPaymentMethod } from "@payment-client/helpers/WalletHelper";
import {
    isNetBankingPaymentMethod,
    isCardPaymentMethod,
    getPaymentsCheckoutState,
    isNewCardPaymentMethod,
} from "@payment-client/helpers/PaymentHelper";
import { isAmazonWallet, getAmazonPaySdkParams, isPhonePePaymentMethod } from "@swiggy-private/payment-ui";
import { isPayLaterMethod } from "@payment-client/helpers/PaylaterHelper";
import { isInAppUpiMethodInOrderData } from "@payment-client/helpers/InAppUpiHelper";

//contexts
import { AmazonPaySdkContext } from "@payment-client/contexts/AmazonPaySdkContext";
import {
    CheckoutContext,
    CheckoutRoute,
    NEWRELIC_TABLES,
    recordNREvent,
    useNavigate,
} from "@swiggy-private/checkout-core";
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";

//hooks

//types
import { CheckoutActionTypes, AnalyticsContext, FailedOrderActionPayload } from "@swiggy-private/checkout-core";
import {
    PaymentFallbacksApiResponse,
    PaymentMethodDef,
    PaymentMethodMeta,
    PaymentMethod,
} from "@swiggy-private/payment-ui";

// constants
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-client/constants/Order";
import {
    NETBANKING_PAYMENT_METHOD_CODE,
    PHONEPE_PLACE_ORDER_TYPE_KEY,
    PHONEPE_PLACE_ORDER_TYPE,
    OnPayExtraParams,
} from "@swiggy-private/payment-core";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { paymentFailedDialogScreenViewEvent } from "@payment-client/analytics/events/PaymentFailed";

interface PaymentFallbackAlertProps {
    fallbackApiMethods: PaymentFallbacksApiResponse;
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    selectAction: (method: PaymentMethod<PaymentMethodMeta>) => void;
    payload: FailedOrderActionPayload;
}

const PaymentFallbackAlert = (props: PaymentFallbackAlertProps): ReactElement | null => {
    const { fallbackApiMethods, payAction, payload, selectAction } = props;
    const { screenViewEvent } = useContext(AnalyticsContext);
    const { state: amazonPaySdkStatus } = useContext(AmazonPaySdkContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { placeOrderData } = payload;
    const navigateTo = useNavigate();
    const { paylaterState } = useContext(PaylaterLinkedContext);

    useEffectOnce(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.NEW_PAYMENT_FAILURE_HALF_CARD_RENDERED,
        });

        // send screen view event
        paymentFailedDialogScreenViewEvent(screenViewEvent);
    });

    const handleOnPay = useCallback(
        (methodData: PaymentMethodDef, isSplitPaySelected: boolean) => {
            let extraParams: OnPayExtraParams = {};

            if (methodData.lastUsedPm && isCardPaymentMethod(methodData)) {
                if (placeOrderData?.extraParams?.cvv) {
                    extraParams = {
                        cvv: placeOrderData?.extraParams?.cvv ?? undefined,
                    };
                }
            } else if (isNewCardPaymentMethod(methodData)) {
                if (methodData.enabled) {
                    const checkoutState = getPaymentsCheckoutState();
                    setCheckoutState({ type: checkoutState });
                    selectAction(methodData);
                }
                return;
            } else if (isPayLaterMethod(methodData)) {
                const payLaterLinkedData = paylaterState[methodData.name];
                if (!payLaterLinkedData.linked) {
                    const checkoutState = getPaymentsCheckoutState();
                    setCheckoutState({ type: checkoutState });
                    navigateTo(CheckoutRoute.PAYLATER_LINKING_CONFIRMATION);
                    return;
                }
            } else if (isNetBankingPaymentMethod(methodData)) {
                extraParams = {
                    bankCode: methodData.nbCode,
                    bankName: methodData.name,
                    merchantId: methodData.groupMeta?.merchantId,
                    paymentMethodType: NETBANKING_PAYMENT_METHOD_CODE,
                };
            } else if (isWalletPaymentMethod(methodData)) {
                if (isPhonePePaymentMethod(methodData)) {
                    extraParams = {
                        [PHONEPE_PLACE_ORDER_TYPE_KEY]: PHONEPE_PLACE_ORDER_TYPE.INSTANT_DEBIT,
                    };
                }
                if (isAmazonWallet(methodData) && amazonPaySdkStatus) {
                    extraParams = getAmazonPaySdkParams(amazonPaySdkStatus.isBalanceSufficient);
                }
            }

            extraParams[PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT] = isSplitPaySelected
                ? fallbackApiMethods.splitpay?.swiggyPayEligibleBalance
                : undefined;

            extraParams["force_validate_coupon"] = fallbackApiMethods.couponApplied && methodData.couponApplicable;

            payAction(methodData, extraParams);
        },
        [
            fallbackApiMethods.splitpay?.swiggyPayEligibleBalance,
            fallbackApiMethods.couponApplied,
            payAction,
            placeOrderData?.extraParams?.cvv,
            setCheckoutState,
            selectAction,
            paylaterState,
            navigateTo,
            amazonPaySdkStatus,
        ],
    );

    // Handles failure alert action click based on fallback payment code
    const onOverlayClose = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
    }, [setCheckoutState]);

    const onOverlayCloseAndReload = useCallback(() => {
        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
            payload: {
                reload: isInAppUpiMethodInOrderData(payload?.failedOrder),
            },
        });
    }, [payload?.failedOrder, setCheckoutState]);

    return (
        <FallbackAlert
            hideOnOverlayClick={true}
            onClose={onOverlayClose}
            onCloseAndReload={onOverlayCloseAndReload}
            fallbackApiMethods={fallbackApiMethods}
            payAction={handleOnPay}
        />
    );
};

export default memo(PaymentFallbackAlert);
